import { useEffect, useState } from "react";

type State = {
  rate: number | null;
  loading: boolean;
  error: string | null;
  fetchedAt: Date | null;
};

const CACHE_KEY = "fx_usd_npr_v1";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Fetch live USD -> target currency exchange rate.
 * Uses open.er-api.com (no API key required).
 * Falls back to a sane default if the API is unreachable.
 */
export function useFxRate(target: string = "NPR", fallback: number = 133) {
  const [state, setState] = useState<State>({
    rate: null,
    loading: true,
    error: null,
    fetchedAt: null,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Try cache first
      try {
        const cached = localStorage.getItem(`${CACHE_KEY}_${target}`);
        if (cached) {
          const parsed = JSON.parse(cached) as { rate: number; ts: number };
          if (Date.now() - parsed.ts < CACHE_TTL_MS) {
            setState({
              rate: parsed.rate,
              loading: false,
              error: null,
              fetchedAt: new Date(parsed.ts),
            });
            return;
          }
        }
      } catch {
        /* ignore cache errors */
      }

      try {
        const res = await fetch(`https://open.er-api.com/v6/latest/USD`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const rate = data?.rates?.[target];
        if (typeof rate !== "number") throw new Error("Rate not found");
        if (cancelled) return;
        const ts = Date.now();
        localStorage.setItem(
          `${CACHE_KEY}_${target}`,
          JSON.stringify({ rate, ts }),
        );
        setState({ rate, loading: false, error: null, fetchedAt: new Date(ts) });
      } catch (err) {
        if (cancelled) return;
        setState({
          rate: fallback,
          loading: false,
          error: err instanceof Error ? err.message : "Unknown error",
          fetchedAt: null,
        });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [target, fallback]);

  return state;
}
